import { Plugin, Widget } from 'ckeditor5'
import React from 'react'
import { createRoot } from 'react-dom/client'
import { toWidget } from 'ckeditor5'
import { PixelQuest } from '@minicoursegenerator/pixel-quest'

// Transform config from editor format to PixelQuest format
// Unwraps _hide/_value wrappers and converts radio options to array with correctAnswerIndex
const transformConfigForPixelQuest = (config) => {
  if (!config) return config

  const result = { ...config }

  // Unwrap mapGrid (extract _value if exists)
  if (result.mapGrid && result.mapGrid._value !== undefined) {
    result.mapGrid = result.mapGrid._value
  }

  // Unwrap startPosition (extract _value if exists)
  if (result.startPosition && result.startPosition._value !== undefined) {
    result.startPosition = result.startPosition._value
  }

  // Transform npcs options from radio format to array with correctAnswerIndex
  if (result.npcs && Array.isArray(result.npcs)) {
    result.npcs = result.npcs.map(npc => {
      const transformedNpc = { ...npc }

      // Convert radio options to array format
      if (npc.options && npc.options._type === 'radio') {
        const options = npc.options._options || []
        const correctAnswer = npc.options._value
        const correctAnswerIndex = options.indexOf(correctAnswer)

        transformedNpc.options = options
        transformedNpc.correctAnswerIndex = correctAnswerIndex >= 0 ? correctAnswerIndex : 0
      }

      return transformedNpc
    })
  }

  return result
}

// Default level JSON for Pixel Quest
const DEFAULT_LEVEL_JSON = {
  title: 'The Path of the Product Bridge',
  mapGrid: {
    _hide: true,
    _value: [
      1, 1, 1, 1, 1, 1, 1, 1, 1, 1,
      1, 3, 3, 3, 1, 1, 1, 1, 1, 1,
      1, 1, 1, 3, 1, 1, 1, 1, 1, 1,
      1, 1, 1, 3, 3, 3, 3, 1, 1, 1,
      1, 1, 1, 1, 1, 1, 3, 1, 1, 1,
      1, 1, 3, 3, 3, 3, 3, 1, 1, 1,
      1, 1, 3, 1, 1, 1, 1, 1, 1, 1,
      1, 1, 3, 3, 3, 3, 1, 1, 1, 1,
      1, 1, 1, 1, 1, 3, 3, 9, 1, 1,
      1, 1, 1, 1, 1, 1, 1, 1, 1, 1,
    ],
  },
  startPosition: {
    _hide: true,
    _value: { x: 1, y: 1 },
  },
  config: {
    language: 'en',
    pathColor: '#374151',
    wallColor: '#111827',
    bgColor: '#020617',
    titleColor: '#facc15',
    exitText: 'EXIT',
    instructionText: 'ANSWER ALL MASTERS CORRECTLY TO UNLOCK THE EXIT',
    victoryText: 'QUEST COMPLETE!',
    gameOverText: 'GAME OVER',
    tryAgainText: 'TRY AGAIN',
  },
  npcs: [
    {
      id: 1,
      name: 'The Architect',
      spriteColor: '#3b82f6',
      position: {
        x: 3,
        y: 1,
      },
      question: 'Product Management acts as a bridge between which two historically misaligned groups?',
      options: {
        _type: 'radio',
        _options: [
          'Investors and Competitors',
          'Engineers and Business Leaders',
          'Project Managers and Designers',
          'HR and Marketing',
        ],
        _value: 'Engineers and Business Leaders',
      },
    },
    {
      id: 2,
      name: 'The Intersection Sage',
      spriteColor: '#ef4444',
      position: {
        x: 5,
        y: 3,
      },
      question: "According to the Intersection Model, what happens if you ignore the 'Technology' domain?",
      options: {
        _type: 'radio',
        _options: [
          'The company goes bankrupt',
          'Nobody uses the product',
          'The product cannot be built',
          'The product has no value proposition',
        ],
        _value: 'The product cannot be built',
      },
    },
    {
      id: 3,
      name: 'The Lean Strategist',
      spriteColor: '#10b981',
      position: {
        x: 4,
        y: 5,
      },
      question: 'What is the primary purpose of a Minimum Viable Product (MVP)?',
      options: {
        _type: 'radio',
        _options: [
          'To release a half-baked product quickly',
          'To save money on development costs',
          'To collect maximum validated learning with least effort',
          'To provide all features to early adopters',
        ],
        _value: 'To collect maximum validated learning with least effort',
      },
    },
    {
      id: 4,
      name: 'The Visionary Guide',
      spriteColor: '#f59e0b',
      position: {
        x: 4,
        y: 7,
      },
      question: 'Which of these is a key question asked by a Product Manager rather than a Project Manager?',
      options: {
        _type: 'radio',
        _options: [
          'How do we get this done by Friday?',
          'Should we build this?',
          'How do we allocate these resources?',
          'Is the budget adherence correct?',
        ],
        _value: 'Should we build this?',
      },
    },
  ],
}

export default class PixelQuestPlugin extends Plugin {
  static get pluginName() {
    return 'PixelQuestPlugin'
  }

  static get requires() {
    return [Widget]
  }

  constructor(editor) {
    super(editor)
    this.mountedRoots = new Map()
    this.domElementMap = new Map() // modelElement -> domElement mapping
    this._updateTimeout = null
    this._lastConfigStr = null
  }

  init() {
    const editor = this.editor

    editor.model.schema.register('pixelQuest', {
      isObject: true,
      allowWhere: '$block',
      allowIn: 'column',
      allowAttributes: [
        'id',
        'data',
        'gameConfig',
      ],
    })

    this._defineConverters()
    this._setupAttributeListener()

    // Pixel Quest seçiliyken klavye ile silinmesini engelle
    editor.editing.view.document.on(
      'keydown',
      (evt, data) => {
        const selection = editor.model.document.selection
        const selectedElement = selection.getSelectedElement()

        if (selectedElement && selectedElement.name === 'pixelQuest') {
          const domEvent = data.domEvent
          const key = domEvent.key

          // Allow: Escape, Arrow keys, Tab
          const allowedKeys = [
            'Escape',
            'ArrowUp',
            'ArrowDown',
            'ArrowLeft',
            'ArrowRight',
            'Tab',
          ]
          if (allowedKeys.includes(key)) {
            return
          }

          // Block: Delete, Backspace, and printable characters
          if (
            key === 'Delete' ||
            key === 'Backspace' ||
            (key.length === 1 && !domEvent.ctrlKey && !domEvent.metaKey)
          ) {
            evt.stop()
            data.preventDefault()
          }
        }
      },
      { priority: 'highest' },
    )

    editor.commands.add('insertPixelQuest', {
      execute: () => {
        editor.model.change(writer => {
          const simpleBoxWrapper = writer.createElement('simpleBoxWrapper', {
            backgroundColor: '#020617',
          })
          const simpleBox = writer.createElement('simpleBox')
          const column = writer.createElement('column')
          const pixelQuest = writer.createElement('pixelQuest', {
            gameConfig: JSON.stringify(DEFAULT_LEVEL_JSON),
          })

          writer.append(simpleBox, simpleBoxWrapper)
          writer.append(column, simpleBox)
          writer.append(pixelQuest, column)

          editor.model.insertContent(simpleBoxWrapper)
          writer.setSelection(pixelQuest, 'on')
        })
      },
    })

    editor.ui.componentFactory.add('insertPixelQuest', locale => {
      const button = document.createElement('button')
      button.className = 'ck-button ck-button_with-text'
      button.style.display = 'flex'
      button.style.alignItems = 'center'
      button.style.gap = '4px'
      button.style.padding = '8px 12px'

      const icon = document.createElement('span')
      icon.textContent = '🎮'
      icon.style.fontSize = '16px'

      const text = document.createTextNode('Pixel Quest')

      button.appendChild(icon)
      button.appendChild(text)

      button.onclick = () => {
        editor.execute('insertPixelQuest')
      }

      return {
        element: button,
        render: () => { },
        destroy: () => { },
      }
    })
  }

  _setupAttributeListener() {
    const editor = this.editor

    // gameConfig attribute değişikliklerini dinle
    editor.model.document.on('change:data', () => {
      const changes = editor.model.document.differ.getChanges()

      for (const change of changes) {
        if (change.type === 'attribute' && change.attributeKey === 'gameConfig') {
          const modelElement = change.range.start.nodeAfter
          if (modelElement && modelElement.name === 'pixelQuest') {
            const newGameConfig = modelElement.getAttribute('gameConfig')
            this._updatePixelQuestComponent(modelElement, newGameConfig)
          }
        }
      }
    })
  }

  _updatePixelQuestComponent(modelElement, gameConfig) {
    // Skip if config hasn't changed
    if (gameConfig === this._lastConfigStr) {
      return
    }

    // Skip color-only changes to prevent excessive re-renders
    // Only update on significant changes (not just bgColor tweaks during color picking)
    try {
      const newConfig = gameConfig ? JSON.parse(gameConfig) : null
      const oldConfig = this._lastConfigStr ? JSON.parse(this._lastConfigStr) : null

      if (newConfig && oldConfig) {
        // Create copies without config.bgColor for comparison
        const newWithoutBg = { ...newConfig, config: { ...newConfig.config } }
        const oldWithoutBg = { ...oldConfig, config: { ...oldConfig.config } }
        delete newWithoutBg.config.bgColor
        delete oldWithoutBg.config.bgColor

        // If only bgColor changed, skip re-render (CSS handles visual update)
        if (JSON.stringify(newWithoutBg) === JSON.stringify(oldWithoutBg)) {
          this._lastConfigStr = gameConfig
          return
        }
      }
    } catch (e) {
      // If parsing fails, continue with normal update
    }

    this._lastConfigStr = gameConfig

    // Debounce: Clear previous timeout and set new one
    if (this._updateTimeout) {
      clearTimeout(this._updateTimeout)
    }

    this._updateTimeout = setTimeout(() => {
      const editor = this.editor

      // View element'i bul
      const viewElement = editor.editing.mapper.toViewElement(modelElement)
      if (!viewElement) return

      // DOM element'i bul
      const domConverter = editor.editing.view.domConverter
      const containerDom = domConverter.mapViewToDom(viewElement)
      if (!containerDom) return

      // React placeholder'ı bul
      const placeholder = containerDom.querySelector('.pixel-quest-react-placeholder')
      if (!placeholder) return

      // Mevcut root'u al ve yeniden render et
      const existingRoot = this.mountedRoots.get(placeholder)
      if (existingRoot) {
        try {
          const parsedConfig = gameConfig ? JSON.parse(gameConfig) : DEFAULT_LEVEL_JSON
          const transformedConfig = transformConfigForPixelQuest(parsedConfig)
          const props = {
            data: transformedConfig,
            onComplete: () => console.log('🎮 Victory!'),
            onFail: () => console.log('🎮 Failed!'),
            onChange: state => console.log('🎮 State:', state),
          }

          console.log('🎮 PixelQuest re-rendering with new config:', props)
          existingRoot.render(React.createElement(PixelQuest, props))
        } catch (error) {
          console.error('Error re-rendering PixelQuest component:', error)
        }
      }
    }, 500) // 500ms debounce - increased for stability
  }

  _defineConverters() {
    const editor = this.editor
    const conversion = editor.conversion

    conversion.for('upcast').elementToElement({
      view: {
        name: 'div',
        attributes: {
          'data-type': 'pixel-quest',
        },
      },
      model: (viewElement, { writer }) => {
        const id = viewElement.getAttribute('id')
        const data = viewElement.getAttribute('data') || ''
        const gameConfig = viewElement.getAttribute('data-game-config') || ''

        const attrs = {
          data,
          gameConfig,
        }
        if (id) attrs.id = id

        return writer.createElement('pixelQuest', attrs)
      },
    })

    conversion.for('dataDowncast').elementToElement({
      model: 'pixelQuest',
      view: (modelElement, { writer }) => {
        const id = modelElement.getAttribute('id')
        const data = modelElement.getAttribute('data') || ''
        const gameConfig = modelElement.getAttribute('gameConfig') || ''

        const attrs = {
          'data-type': 'pixel-quest',
          data: data,
          'data-game-config': gameConfig,
        }

        if (id !== undefined && id !== null) {
          attrs.id = id
        }

        return writer.createEmptyElement('div', attrs)
      },
    })

    conversion.for('editingDowncast').elementToElement({
      model: 'pixelQuest',
      view: (modelElement, { writer }) => {
        const gameConfig = modelElement.getAttribute('gameConfig') || ''

        const containerAttrs = {
          class: 'pixel-quest-widget-container',
          'data-type': 'pixel-quest',
          'data-game-config': gameConfig,
        }

        const container = writer.createContainerElement('div', containerAttrs)

        const placeholder = writer.createRawElement(
          'div',
          {
            class: 'pixel-quest-react-placeholder',
          },
          domElement => {
            // Mount React component
            console.log('🎮 Pixel Quest rendering started')
            try {
              const parsedConfig = gameConfig ? JSON.parse(gameConfig) : DEFAULT_LEVEL_JSON
              const transformedConfig = transformConfigForPixelQuest(parsedConfig)
              const props = {
                data: transformedConfig,
                onComplete: () => console.log('🎮 Victory!'),
                onFail: () => console.log('🎮 Failed!'),
                onChange: state => console.log('🎮 State:', state),
              }

              console.log('🎮 PixelQuest Props:', props)

              const root = createRoot(domElement)
              root.render(React.createElement(PixelQuest, props))

              editor.plugins
                .get('PixelQuestPlugin')
                .mountedRoots.set(domElement, root)
            } catch (error) {
              console.error('Error rendering PixelQuest component:', error)
              domElement.innerHTML = `
                <div style="padding: 20px; background: #f5f5f5; border-radius: 8px; text-align: center;">
                  <span style="font-size: 24px;">🎮</span>
                  <p>Pixel Quest</p>
                  <small>Component not available</small>
                </div>
              `
            }
          },
        )

        writer.insert(writer.createPositionAt(container, 0), placeholder)

        return toWidget(container, writer, {
          label: 'Pixel Quest',
          hasSelectionHandle: true,
        })
      },
    })
  }

  destroy() {
    // Clear debounce timeout
    if (this._updateTimeout) {
      clearTimeout(this._updateTimeout)
      this._updateTimeout = null
    }

    for (const [domElement, root] of this.mountedRoots) {
      try {
        root.unmount()
      } catch (error) {
        console.error('Error unmounting React component:', error)
      }
    }
    this.mountedRoots.clear()
    super.destroy()
  }
}

export { PixelQuestPlugin }
